import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Delete as DeleteIcon,
  DeleteSweep as DeleteAllIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";

import { 
  fetchPendingResponses, 
  deletePendingResponse 
} from '../../store/surveyResponsesSlice';
import { getApiUrl } from '../../config/api';
import API_CONFIG from '../../config/api';

import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import './Progress.mobile.css';

const Progress = () => {
  const dispatch = useDispatch();
  const { 
    currentSurveyId: surveyId,
    pendingResponses, 
    loading: { pendingResponses: loading },
    error: { pendingResponses: error }
  } = useSelector(state => state.surveyResponses);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [success, setSuccess] = useState(null);
  const [localError, setError] = useState(null);

  const handleDelete = async (responseId) => {
    try {
      await dispatch(deletePendingResponse(responseId)).unwrap();
      setDeleteConfirm(null);
      setSuccess('Response deleted successfully');
    } catch (error) {
      setError(error);
      setDeleteConfirm(null);
    }
  };

  const handleDeleteAll = async () => {
    try {
      const response = await fetch(
        getApiUrl(API_CONFIG.ENDPOINTS.SURVEYS.DELETE_ALL_RESPONSES),
        { 
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ survey_id: surveyId }),
          mode: 'cors',
          credentials: 'omit'
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete responses');
      }

      const data = await response.json();

      if (data.status === 'success') {
        // Refetch pending responses to update the state
        dispatch(fetchPendingResponses(surveyId));
        setDeleteAllConfirm(false);
        setSuccess(data.message || 'All responses deleted successfully');
      } else {
        throw new Error(data.message || 'Failed to delete responses');
      }
    } catch (error) {
      console.error('Delete all error:', error);
      setError(error.message);
      setDeleteAllConfirm(false);
    }
  };

  const formatBrowserInfo = (browserString) => {
    if (!browserString) return 'Unknown';
    if (browserString.includes('Chrome')) return 'Chrome';
    if (browserString.includes('Firefox')) return 'Firefox';
    if (browserString.includes('Safari')) return 'Safari';  
    if (browserString.includes('Edge')) return 'Edge';
    return 'Other';
  };

  const formatOSInfo = (browserString) => {
    if (!browserString) return 'Unknown';
    if (browserString.includes('Windows NT 10.0')) return 'Windows 10';
    if (browserString.includes('Windows NT 6.1')) return 'Windows 7';
    if (browserString.includes('Macintosh')) return 'macOS';
    if (browserString.includes('Linux')) return 'Linux';
    return 'Other';
  };

  const getDisplayRows = (data) => {
    if (!data || data.length === 0) return [];
    
    const numEmptyRows = Math.max(10 - data.length, 0);
    
    const emptyRows = Array(numEmptyRows).fill({
      id: '',
      respondent_email: '',
      browser: '',
      location: '',
      status: '',
    });

    return [...data, ...emptyRows];
  };

  const filteredAndSortedResponses = pendingResponses
    .filter(response => 
      response.respondent_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      response.id?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const handleRetry = () => {
    dispatch(fetchPendingResponses(surveyId));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert 
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {success && (
        <Alert 
          severity="success"
          onClose={() => setSuccess(null)}
          sx={{ mb: 2 }}
        >
          {success}
        </Alert>
      )}
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#11998e' }}>
          Incomplete Responses
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#11998e' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: '280px',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'rgba(17, 153, 142, 0.04)',
                '& fieldset': {
                  borderColor: 'rgba(17, 153, 142, 0.2)'
                },
                '&:hover fieldset': {
                  borderColor: '#11998e'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#11998e',
                  borderWidth: '2px'
                }
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(17, 153, 142, 0.7)',
                opacity: 1
              }
            }}
          />
          
          {pendingResponses.length > 0 && (
            <Button
              startIcon={<DeleteAllIcon />}
              color="error"
              variant="outlined"
              size="small"
              onClick={() => setDeleteAllConfirm(true)}
              sx={{
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.04)'
                }
              }}
            >
              Delete All
            </Button>
          )}
        </Box>
      </Box>

      <Card sx={{
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}>
        <CardContent>
          <DataTable
            value={getDisplayRows(filteredAndSortedResponses)}
            paginator
            rows={10}
            globalFilter={searchTerm}
            emptyMessage="No incomplete responses found"
            stripedRows
            showGridlines
            responsiveLayout="scroll"
            className="p-datatable-sm"
            tableStyle={{ minWidth: '50rem' }}
          >
            <Column 
              header="No." 
              body={(data, options) => options.rowIndex + 1}
              style={{ width: '50px' }}
            />
            <Column 
              field="respondent_email" 
              header="Email" 
              sortable 
              body={(rowData) => rowData.respondent_email || ''}
              style={{ minWidth: '200px' }}
            />
            <Column 
              field="browser" 
              header="Browser"
              body={(rowData) => rowData.browser ? formatBrowserInfo(rowData.browser) : ''}
              style={{ minWidth: '120px' }}
            />
            <Column 
              field="browser" 
              header="OS"
              body={(rowData) => rowData.browser ? formatOSInfo(rowData.browser) : ''}
              style={{ minWidth: '120px' }}
            />
            <Column 
              field="location" 
              header="Location"
              body={(rowData) => rowData.location || ''}
              style={{ minWidth: '150px' }}
            />
            <Column 
              field="status" 
              header="Status"
              body={(rowData) => rowData.status ? (
                <Chip 
                  label={rowData.status === 'started' ? 'Started' : 'Pending'}
                  size="small"
                  sx={{ 
                    bgcolor: rowData.status === 'started' ? 'info.lighter' : 'warning.lighter',
                    color: rowData.status === 'started' ? 'info.dark' : 'warning.dark',
                    fontWeight: 500
                  }}
                />
              ) : ''}
              style={{ minWidth: '120px' }}
            />
            <Column 
              header="Actions"
              body={(rowData) => (
                <IconButton 
                  size="small" 
                  color="error"
                  onClick={() => setDeleteConfirm(rowData.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              style={{ width: '80px', textAlign: 'center' }}
            />
          </DataTable>
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this response? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          pb: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1
        }}>
          <Button 
            onClick={() => setDeleteConfirm(null)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(deleteConfirm)}
            variant="contained"
            color="error"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteAllConfirm}
        onClose={() => setDeleteAllConfirm(false)}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Delete All Responses
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all incomplete responses? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          pb: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1
        }}>
          <Button 
            onClick={() => setDeleteAllConfirm(false)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAll}
            variant="contained"
            color="error"
            autoFocus
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Progress;